import React from 'react';
import {
  labsHexSectorPathCircularHub,
  labsHexVerticesPointyTop,
  labsOuterEdgeInstrumentTicks,
  labsVertexOutwardTick,
} from './labsMarkGeometry';
import {
  LABS_DIAGRAM_LINE,
  LABS_DIAGRAM_LINE_GUIDE,
  LABS_DIAGRAM_LINE_VERTEX,
  LABS_DIAGRAM_SECTOR_FILLS,
} from './labsDiagramPalette';

/**
 * Shared SVG pieces for the Labs mark (hero + entity icon).
 * Neutral grey panels, circular hub void, soft strokes — professional research-lab read.
 *
 * @param {{ bgGradientId?: string }} [_props] - kept for call sites; mark is flat / no defs.
 */
export function LabsDiagramDefs(_props) {
  return null;
}

function LabsHexSectorMark() {
  const cx = 100;
  const cy = 100;
  const holeR = 18;
  const outerR = 58;
  const midGuideR = (holeR + outerR) / 2;
  const indent = 0.13;
  const sectorStroke = 0.62;
  const tickStrokeMajor = 0.82;
  const tickStrokeMinor = 0.55;
  const vertexTickLen = 3.2;
  const vertexStroke = 0.5;
  const guideStroke = 0.48;

  const outerVerts = labsHexVerticesPointyTop(cx, cy, outerR);

  const instrumentTickOpts = {
    majorInward: 10.4,
    minorInward: 5.1,
    chordOffset: 5,
  };

  const instrumentLineData = [0, 1, 2, 3, 4, 5].flatMap((edgeIndex) => {
    const segs = labsOuterEdgeInstrumentTicks(cx, cy, outerVerts, edgeIndex, instrumentTickOpts);
    return segs.map((seg, si) => ({
      lineKey: `labs-tick-${edgeIndex}-${si}`,
      seg,
      isMajor: si === 0,
    }));
  });

  return (
    <g aria-hidden="true">
      {[0, 1, 2, 3, 4, 5].map((edgeIndex) => (
        <path
          key={`labs-sector-${edgeIndex}`}
          d={labsHexSectorPathCircularHub(cx, cy, holeR, outerVerts, edgeIndex, indent)}
          fill={LABS_DIAGRAM_SECTOR_FILLS[edgeIndex]}
          stroke={LABS_DIAGRAM_LINE}
          strokeWidth={sectorStroke}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      ))}

      <circle
        cx={cx}
        cy={cy}
        r={midGuideR}
        fill="none"
        stroke={LABS_DIAGRAM_LINE_GUIDE}
        strokeWidth={guideStroke}
        strokeLinejoin="round"
      />

      {instrumentLineData.map(({ lineKey, seg, isMajor }) => (
        <line
          key={lineKey}
          x1={seg.x1}
          y1={seg.y1}
          x2={seg.x2}
          y2={seg.y2}
          stroke={LABS_DIAGRAM_LINE}
          strokeWidth={isMajor ? tickStrokeMajor : tickStrokeMinor}
          strokeLinecap="round"
        />
      ))}

      {outerVerts.map((v, vi) => {
        const seg = labsVertexOutwardTick(cx, cy, v.x, v.y, vertexTickLen);
        return (
          <line
            key={`labs-vtx-${vi}`}
            x1={seg.x1}
            y1={seg.y1}
            x2={seg.x2}
            y2={seg.y2}
            stroke={LABS_DIAGRAM_LINE_VERTEX}
            strokeWidth={vertexStroke}
            strokeLinecap="round"
          />
        );
      })}
    </g>
  );
}

/** @param {{ bgGradientId?: string }} [_props] - kept for call sites. */
export function LabsDiagramBody(_props) {
  return <LabsHexSectorMark />;
}
